/**
 * Workflows (ADR-0025, mockup outputs/workflows-mockup-20261029.html): every open workflow across
 * people, the company, TechHub clients and (later) vendors, plus "Start a workflow".
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, type WfListItem, type WfPerson, type WfTemplate } from "../../lib/api";
import { useStore } from "../../lib/store";
import { coName } from "../../lib/brand";
import { fmt } from "../../lib/format";
import { Loading } from "../../components/bits";
import { useSort } from "../../components/Sort";
import { daysBetween, todayEastern } from "../../../../shared/agreements";
import { SeeHow } from "../../components/SeeHow";

const ON_LABEL: Record<string, string> = { person: "Person", company: "Company", client: "Client" };
type Filter = "open" | "overdue" | "mine" | "done";

export function WorkflowsPage() {
  const { toast, p, me } = useStore();
  const [filter, setFilter] = useState<Filter>("open");
  const [rows, setRows] = useState<WfListItem[] | null>(null);
  const [q, setQ] = useState("");
  const [starting, setStarting] = useState(false);
  const load = () => api.workflows(filter === "done" ? "done" : "open").then((r) => setRows(r.workflows), (e: Error) => toast(e.message));
  useEffect(() => { setRows(null); void load(); }, [filter === "done"]);
  const today = todayEastern();
  const late = (w: WfListItem) => w.status === "open" && !!w.due_on && daysBetween(today, w.due_on) < 0;
  const shown = (rows ?? []).filter((w) => (filter === "overdue" ? late(w) : filter === "mine" ? w.mine > 0 : true))
    .filter((w) => !q || `${w.title} ${w.on ?? ""} ${w.kind_label} ${w.company_name}`.toLowerCase().includes(q.toLowerCase()));
  const { sorted, th } = useSort("workflows", shown, {
    name: (w) => w.kind_label, on: (w) => w.on ?? "", progress: (w) => (w.total ? w.done / w.total : 0), next: (w) => w.next?.title ?? "", due: (w) => w.due_on ?? "",
  }, { key: "due", dir: "asc" });
  // Who may start one (ADR-0025): company admins, and a person's manager for that person.
  const adminIn = p.myCodes.filter((c) => p.adminCodes.includes(c));
  const [managerIn, setManagerIn] = useState<string[]>([]);
  useEffect(() => {
    const others = p.myCodes.filter((c) => !adminIn.includes(c));
    void Promise.all(others.map((c) => api.wfPeople(c).then((r) => (r.people.some((x) => x.manager_user_id === me.user.id) ? c : null), () => null)))
      .then((codes) => setManagerIn(codes.filter((c): c is string => !!c)));
  }, [p.myCodes.join(","), adminIn.join(",")]);
  const startCodes = p.myCodes.filter((c) => adminIn.includes(c) || managerIn.includes(c));
  const canStart = startCodes.length > 0;
  const n = (f: Filter) => (rows ?? []).filter((w) => (f === "overdue" ? late(w) : f === "mine" ? w.mine > 0 : true)).length;

  return (
    <div className="view">
      <div className="hgroup">
        <div>
          <div className="eyebrow">Vault · {p.myCodes.length > 1 ? "All my companies" : coName(p.myCodes[0] ?? "")}</div>
          <h1 className="title">Workflows</h1>
          <div className="lede">Checklists that run themselves: each step goes to the right person's My tasks, ticks itself when DocVault can see it's done, and the whole thing starts when something happens.</div>
        </div>
        <div className="row-btns"><SeeHow video="onboarding" label="See how onboarding works" />{canStart && <button className="btn btn-accent" onClick={() => setStarting(true)}>+ Start a workflow</button>}</div>
      </div>
      {starting && <StartWorkflow codes={startCodes} adminIn={adminIn} onClose={() => setStarting(false)} />}
      <div className="x-filters wf-filters">
        <input className="inp wf-search" placeholder="Find a workflow, person or client" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find" />
        {(["open", "overdue", "mine", "done"] as Filter[]).map((f) => (
          <button key={f} className={`x-pill ${filter === f ? "on" : ""}`} onClick={() => setFilter(f)}>{f === "open" ? "Open" : f === "overdue" ? "Overdue" : f === "mine" ? "Mine" : "Finished"}{f !== "done" && rows ? ` ${n(f)}` : ""}</button>
        ))}
      </div>
      {!rows ? <Loading /> : !sorted.length ? (
        <div className="card empty">{filter === "open" ? "No open workflows. Onboarding starts by itself when someone is added on the Team page." : "Nothing here."}</div>
      ) : (
        <div className="card tbl-wrap">
          <table className="tbl wf-tbl">
            <thead><tr>{th("name", "Workflow")}{th("on", "On")}{th("progress", "Progress")}{th("next", "Next step", "hide-sm")}{th("due", "Due")}</tr></thead>
            <tbody>
              {sorted.map((w) => (
                <tr key={w.id}>
                  <td><Link className="doclink" to={w.subject === "client" ? `/clienthub/c/${w.client_id}?tab=ck` : `/workflows/${w.id}`}><b>{w.kind_label}</b></Link>
                    <div className="cap rh-cap">{w.on}{p.myCodes.length > 1 ? ` · ${w.company_name}` : ""}</div></td>
                  <td><span className={`x-chip wf-on-${w.subject}`}>{ON_LABEL[w.subject] ?? w.subject}</span></td>
                  <td><span className="wf-prog"><span className="bar"><span style={{ width: `${w.total ? (100 * w.done) / w.total : 0}%` }} /></span>{w.done} of {w.total}</span></td>
                  <td className="hide-sm">{w.status !== "open" ? <span className="cap">Finished {w.finished_at ? fmt(w.finished_at) : ""}</span> : w.next ? <>{w.next.title}<span className="cap"> · {w.next.auto ? "ticks itself" : w.next.who}</span></> : null}</td>
                  <td className={late(w) ? "wf-late" : ""}>{w.due_on ? fmt(w.due_on) : <span className="cap">-</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="foot-note">TechHub's checklists (New client setup, Client offboarding, Agreement renewal) are listed here too and open on the client's page. Templates: Settings &gt; Workflows.</div>
    </div>
  );
}

function StartWorkflow({ codes, adminIn, onClose }: { codes: string[]; adminIn: string[]; onClose: () => void }) {
  const { me, toast } = useStore();
  const nav = useNavigate();
  const [co, setCo] = useState(codes[0] ?? "");
  const [tpls, setTpls] = useState<WfTemplate[]>([]);
  const [people, setPeople] = useState<WfPerson[]>([]);
  const [tpl, setTpl] = useState("");
  const [person, setPerson] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!co) return;
    // A manager who isn't a company admin starts person workflows on the people they manage.
    const admin = adminIn.includes(co);
    void api.wfTemplates(co).then((r) => { const list = r.templates.filter((t) => t.active && t.subject !== "client" && (admin || t.subject === "person")); setTpls(list); setTpl(list[0]?.id ?? ""); }, (e: Error) => toast(e.message));
    void api.wfPeople(co).then((r) => setPeople(admin ? r.people : r.people.filter((x) => x.manager_user_id === me.user.id)), () => setPeople([]));
  }, [co]);
  const t = tpls.find((x) => x.id === tpl);
  const start = async () => {
    setBusy(true);
    try { const r = await api.wfStart({ company: co, template_id: tpl, person_id: t?.subject === "person" ? person : undefined }); toast("Started. Each step is in its person's My tasks."); nav(`/workflows/${r.id}`); }
    catch (e) { toast((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div className="card sect wf-start">
      <h3>Start a workflow</h3>
      <div className="form-row">
        {codes.length > 1 && <label><span className="lbl">Company</span><select className="inp" value={co} onChange={(e) => setCo(e.target.value)}>{codes.map((c) => <option key={c} value={c}>{coName(c)}</option>)}</select></label>}
        <label className="grow"><span className="lbl">Workflow</span><select className="inp" value={tpl} onChange={(e) => setTpl(e.target.value)}>{tpls.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></label>
        {t?.subject === "person" && <label className="grow"><span className="lbl">Person</span><select className="inp" value={person} onChange={(e) => setPerson(e.target.value)}>
          <option value="">Pick a person</option>{people.map((x) => <option key={x.user_id} value={x.user_id}>{x.name}</option>)}</select></label>}
      </div>
      {t && <div className="cap" style={{ margin: "6px 0 10px" }}>{t.description} {t.subject === "person" ? "Company admins and the person's manager can start it." : "Company admins start company workflows."}</div>}
      <div className="row-btns"><button className="btn btn-accent" disabled={busy || !tpl || (t?.subject === "person" && !person)} onClick={() => void start()}>Start</button><button className="btn btn-ghost" onClick={onClose}>Cancel</button></div>
    </div>
  );
}
